import { Link, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  CalendarDays,
  CalendarRange,
  CircleAlert,
  Clock,
  MessageSquare,
  Minus,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Timer,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "../hooks/useSession";
import { usePortalKeepAlive } from "../hooks/usePortalKeepAlive";
import { useLivePolling } from "../hooks/useLivePolling";
import { PageHeader } from "../components/layout/PageHeader";
import { customerDashboardApi } from "../api/customerDashboardApi";
import type {
  DashboardInfoDto,
  LiveCallsResponse,
  MonthlyTrafficResponse,
  OutResultsResponse,
  DashboardStatsResponse,
} from "../api/customerDashboardApi";

const LIVE_POLL_MS = 3000;
const ERRORS_POLL_MS = 15000;
const STATS_POLL_MS = 60000;
const ERRORS_WINDOW_MIN = 24 * 60; // last 24 hours

/**
 * Customer dashboard. Layout (top-down):
 *   1. Live row: Current Calls tile + Last Errors (24 h) tile.
 *   2. Per-day inbound minutes graph for the current month (gated on
 *      CUSTOMER.DASHBOARD.WIDGETS.INBOUND, with a "View more" link to
 *      the full inbound report).
 *   3. KPI sections (Inbound, Outbound, Chat) gated on the same
 *      `CUSTOMER.DASHBOARD.WIDGETS.*` permissions the legacy Razor
 *      portal used. Each KPI row is a tile with three compact stat
 *      cards: Today, This Week, This Month.
 *
 * Live tiles (current calls / last errors) own their own polling
 * cadences. The KPI stats are fetched once at the page level and
 * shared between all KPI sections + the chart's "today" bar. The
 * monthly inbound chart is fetched once on mount and never auto
 * refreshes; its current-day bar is updated locally from the inbound
 * KPI numbers.
 */
export function DashboardPage() {
  const { session } = useSession();
  const navigate = useNavigate();

  const kind = session?.kind?.toLowerCase();
  const isUser = kind === "user";
  const isCustomer = kind === "customer";
  const key = isUser
    ? session?.userKey ?? null
    : isCustomer
    ? session?.customerId?.toString() ?? null
    : null;

  usePortalKeepAlive({
    key,
    sessionKey: session?.portalSessionKey ?? null,
    kind: isUser ? "user" : isCustomer ? "customer" : null,
  });

  const permissions = useMemo(
    () => new Set(session?.permissions ?? []),
    [session?.permissions],
  );
  const can = (perm: string) => permissions.has(perm);

  const showLive = can("CUSTOMER.DASHBOARD.WIDGETS.LIVE") || isCustomer;
  const showInbound = can("CUSTOMER.DASHBOARD.WIDGETS.INBOUND");
  const showOutbound = can("CUSTOMER.DASHBOARD.WIDGETS.OUTBOUND");
  const showChat = can("CUSTOMER.DASHBOARD.WIDGETS.CHAT");
  const showAnyKpi = showInbound || showOutbound || showChat;

  // KPI stats are fetched once at the page level and shared with each
  // KpiSection + the inbound minutes chart, instead of every section
  // running its own poll. The chart uses inbound.secondsToday to
  // refresh just the current-day bar without re-fetching the whole
  // monthly traffic report.
  const stats = useLivePolling<DashboardStatsResponse>({
    fetcher: () => customerDashboardApi.stats(),
    intervalMs: STATS_POLL_MS,
  });

  return (
    <>
      <PageHeader title="Dashboard" />
      <div className="dashboard" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {showLive && (
          <div className="tile-grid">
            <CurrentSessionsTile
              onOpenReport={() => navigate("/reports/live/calls")}
              showChat={showChat}
            />
            <LastErrorsTile />
          </div>
        )}

        {showInbound && (
          <MinutesPerDayTile
            todaySecondsOverride={stats.data?.inbound?.secondsToday ?? null}
            todayCallsOverride={stats.data?.inbound?.callsToday ?? null}
          />
        )}

        {showAnyKpi && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {showInbound && (
              <KpiSection
                kind="inbound"
                title="Inbound"
                stats={stats.data}
                loading={stats.loading}
                error={stats.error}
              />
            )}
            {showOutbound && (
              <KpiSection
                kind="outbound"
                title="Outbound"
                stats={stats.data}
                loading={stats.loading}
                error={stats.error}
              />
            )}
            {showChat && (
              <KpiSection
                kind="chat"
                title="Chat"
                stats={stats.data}
                loading={stats.loading}
                error={stats.error}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*                       Shared tile primitives                        */
/* ------------------------------------------------------------------ */

/**
 * The square brand-coloured icon badge used in front of every tile
 * title. Kept as a shared component so all tiles stay visually
 * consistent with the new "rest" theme.
 */
function TileIconSquare({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 32,
        height: 32,
        borderRadius: 10,
        background: "var(--portal-primary-soft)",
        color: "var(--portal-primary-darker)",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

/**
 * The tile header's right-hand "View more / View all" affordance.
 * Reused so the same primary-coloured link style appears on the
 * Current Sessions tile and the inbound minutes graph.
 */
function ViewMoreLink({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        color: "var(--portal-primary-darker)",
        fontSize: "0.85rem",
        fontWeight: 600,
      }}
    >
      {children} <ArrowRight size={14} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*                     Current Sessions (live)                         */
/* ------------------------------------------------------------------ */

function CurrentSessionsTile({
  onOpenReport,
  showChat,
}: {
  onOpenReport: () => void;
  showChat: boolean;
}) {
  // Fetch all live sessions so we can group on the client. The SP is
  // already capped server-side and a customer rarely has more than a
  // few dozen rows in flight, so we accept the full set rather than
  // paging here.
  const { data, loading, error } = useLivePolling<LiveCallsResponse>({
    fetcher: () => customerDashboardApi.liveCalls(),
    intervalMs: LIVE_POLL_MS,
  });

  const calls = data?.calls ?? [];
  const total = data?.total ?? 0;
  const inActive = data?.callsInActive ?? 0;
  const outActive = data?.callsOutActive ?? 0;
  const chatActive = data?.chatsActive ?? 0;

  // Group sessions by DNIS, and inside each group count how many are
  // in each state. Sort groups by total sessions desc so the busiest
  // numbers float to the top, and within a group we display state
  // chips ordered the same way so the visual is stable across polls.
  const groups = useMemo(() => {
    const byDnis = new Map<string, Map<string, number>>();
    for (const c of calls) {
      const dnis = c.dnis || "—";
      const state = (c.state || "").toUpperCase() || "—";
      let stateCounts = byDnis.get(dnis);
      if (!stateCounts) {
        stateCounts = new Map();
        byDnis.set(dnis, stateCounts);
      }
      stateCounts.set(state, (stateCounts.get(state) ?? 0) + 1);
    }
    const out: Array<{ dnis: string; total: number; states: Array<{ state: string; count: number }> }> = [];
    for (const [dnis, stateCounts] of byDnis) {
      const states = Array.from(stateCounts.entries())
        .map(([state, count]) => ({ state, count }))
        .sort((a, b) => b.count - a.count || a.state.localeCompare(b.state));
      const groupTotal = states.reduce((acc, s) => acc + s.count, 0);
      out.push({ dnis, total: groupTotal, states });
    }
    out.sort((a, b) => b.total - a.total || a.dnis.localeCompare(b.dnis));
    return out;
  }, [calls]);

  return (
    <section
      className="tile tile-link"
      role="button"
      tabIndex={0}
      onClick={onOpenReport}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenReport();
        }
      }}
      aria-labelledby="tile-live-sessions"
      style={{ cursor: "pointer" }}
    >
      <header className="tile-head">
        <h3 id="tile-live-sessions" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
          <TileIconSquare>
            <Activity size={18} />
          </TileIconSquare>
          Current sessions
          <span
            style={{
              marginLeft: 8,
              padding: "2px 10px",
              borderRadius: 999,
              background: "var(--portal-primary-soft)",
              color: "var(--portal-primary-darker)",
              fontSize: "0.8rem",
              fontWeight: 700,
            }}
          >
            {total}
          </span>
        </h3>
        <ViewMoreLink>View all</ViewMoreLink>
      </header>

      <div style={{ display: "flex", gap: 14, color: "var(--portal-muted)", fontSize: "0.85rem", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <PhoneIncoming size={14} /> Inbound <strong style={{ color: "var(--portal-text)" }}>{inActive}</strong>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <PhoneOutgoing size={14} /> Outbound <strong style={{ color: "var(--portal-text)" }}>{outActive}</strong>
        </span>
        {showChat && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <MessageSquare size={14} /> Chat <strong style={{ color: "var(--portal-text)" }}>{chatActive}</strong>
          </span>
        )}
      </div>

      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          minHeight: 48,
        }}
      >
        {groups.length === 0 && (
          <li style={{ color: "var(--portal-muted)", padding: "8px 4px", fontSize: "0.9rem" }}>
            {loading
              ? "Loading…"
              : error
              ? "Could not load current sessions."
              : "No active sessions right now."}
          </li>
        )}
        {groups.map((g) => (
          <li
            key={g.dnis}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 8px",
              borderRadius: 10,
            }}
          >
            <span
              style={{
                fontFamily: "var(--portal-font-mono, monospace)",
                fontWeight: 700,
                color: "var(--portal-text)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {g.dnis}
            </span>
            <span
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {g.states.map((s) => (
                <CallStateChip key={s.state} state={s.state} count={s.count} />
              ))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CallStateChip({ state, count }: { state: string; count: number }) {
  const palette = stateChipPalette(state);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        flexShrink: 0,
      }}
    >
      {palette.icon}
      <span>{state || "—"}</span>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          padding: "0 6px",
          marginLeft: 2,
          borderRadius: 999,
          background: "rgba(255,255,255,0.6)",
          color: palette.fg,
        }}
      >
        {count}
      </span>
    </span>
  );
}

function stateChipPalette(state: string): { bg: string; fg: string; icon: React.ReactNode } {
  const upper = (state || "").toUpperCase();
  const palette: Record<string, { bg: string; fg: string; icon: React.ReactNode }> = {
    CONNECTED: { bg: "rgba(34,197,94,0.15)", fg: "#15803d", icon: <PhoneOutgoing size={12} /> },
    RINGING: { bg: "rgba(59,130,246,0.15)", fg: "#1d4ed8", icon: <PhoneIncoming size={12} /> },
    QUEUE: { bg: "rgba(245,158,11,0.18)", fg: "#a16207", icon: <Timer size={12} /> },
    IVR: { bg: "rgba(99,102,241,0.15)", fg: "#4338ca", icon: <Zap size={12} /> },
    "EARLY MEDIA": { bg: "rgba(99,102,241,0.15)", fg: "#4338ca", icon: <Zap size={12} /> },
    IN_CHAT: { bg: "rgba(217,70,239,0.15)", fg: "#86198f", icon: <MessageSquare size={12} /> },
  };
  return palette[upper] ?? { bg: "var(--portal-primary-soft)", fg: "var(--portal-primary-darker)", icon: <Zap size={12} /> };
}

/* ------------------------------------------------------------------ */
/*                       Last Errors (24 hours)                        */
/* ------------------------------------------------------------------ */

function LastErrorsTile() {
  const { data, error, loading } = useLivePolling<OutResultsResponse>({
    fetcher: () => customerDashboardApi.outResults(ERRORS_WINDOW_MIN),
    intervalMs: ERRORS_POLL_MS,
  });

  const total =
    (data?.busy ?? 0) +
    (data?.noAnswer ?? 0) +
    (data?.error ?? 0) +
    (data?.shortCall ?? 0);

  // The four error buckets get a quiet treatment now: no coloured
  // backgrounds, just the brand primary on the value (consistent with
  // the KPI cards) and a small icon. Keeps the dashboard calm while
  // still letting non-zero numbers stand out via the bold primary
  // colour.
  const items: Array<{
    label: string;
    value: number;
    icon: React.ReactNode;
  }> = [
    { label: "Busy", value: data?.busy ?? 0, icon: <PhoneOff size={16} /> },
    { label: "No answer", value: data?.noAnswer ?? 0, icon: <PhoneMissed size={16} /> },
    { label: "Error", value: data?.error ?? 0, icon: <CircleAlert size={16} /> },
    { label: "Short call", value: data?.shortCall ?? 0, icon: <Timer size={16} /> },
  ];

  return (
    <section className="tile" aria-labelledby="tile-last-errors">
      <header className="tile-head">
        <h3 id="tile-last-errors" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
          <TileIconSquare>
            <CircleAlert size={18} />
          </TileIconSquare>
          Last errors
          <span style={{ color: "var(--portal-muted)", fontWeight: 500, fontSize: "0.85rem" }}>
            (24 h)
          </span>
          <span
            style={{
              marginLeft: 8,
              padding: "2px 10px",
              borderRadius: 999,
              background: "var(--portal-primary-soft)",
              color: "var(--portal-primary-darker)",
              fontSize: "0.8rem",
              fontWeight: 700,
            }}
          >
            {total}
          </span>
        </h3>
        {loading && !data && <span style={{ color: "var(--portal-muted)", fontSize: "0.8rem" }}>Loading…</span>}
        {Boolean(error) && <span style={{ color: "#b91c1c", fontSize: "0.8rem" }}>Failed</span>}
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        {items.map((it) => (
          <div
            key={it.label}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              padding: "10px 10px",
              borderRadius: 12,
              background: "transparent",
              border: "1px solid var(--portal-primary-faint)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: "var(--portal-muted)",
                display: "inline-flex",
              }}
            >
              {it.icon}
            </span>
            <span
              style={{
                fontSize: "1.5rem",
                lineHeight: 1.1,
                fontWeight: 700,
                color: "var(--portal-primary)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {it.value}
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "var(--portal-muted)",
                fontWeight: 600,
              }}
            >
              {it.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*                        KPI sections (stats)                         */
/* ------------------------------------------------------------------ */

type KpiKind = "inbound" | "outbound" | "chat";

function KpiSection({
  kind,
  title,
  stats,
  loading,
  error,
}: {
  kind: KpiKind;
  title: string;
  stats: DashboardStatsResponse | null;
  loading: boolean;
  error: unknown;
}) {
  const data = stats;

  const info: DashboardInfoDto | null =
    kind === "inbound" ? data?.inbound ?? null
    : kind === "outbound" ? data?.outbound ?? null
    : data?.chat ?? null;

  // For chat the "calls" are really sessions; the inbound/outbound
  // KPI tiles count phone calls. Used as the secondary line on each
  // stat card.
  const callsLabel = kind === "chat" ? "Sessions" : "Calls";

  const headIcon =
    kind === "inbound" ? <PhoneIncoming size={18} />
    : kind === "outbound" ? <PhoneOutgoing size={18} />
    : <MessageSquare size={18} />;

  return (
    <section className="tile" aria-label={`${title} statistics`}>
      <header className="tile-head">
        <h3 style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "var(--portal-primary-soft)",
              color: "var(--portal-primary-darker)",
              display: "grid",
              placeItems: "center",
            }}
          >
            {headIcon}
          </span>
          {title}
        </h3>
        {loading && !data && <span style={{ color: "var(--portal-muted)", fontSize: "0.8rem" }}>Loading…</span>}
        {Boolean(error) && <span style={{ color: "#b91c1c", fontSize: "0.8rem" }}>Failed</span>}
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <KpiCard
          icon={<Clock size={14} />}
          label="Today"
          primary={formatMinutes(info?.secondsToday ?? 0)}
          secondary={`${info?.callsToday ?? 0} ${callsLabel.toLowerCase()}`}
          trend={info?.trendToday ?? 0}
          comparison={
            info?.todayCompare !== undefined && info.todayCompare !== 0
              ? `vs yesterday ${formatMinutes(info.todayCompare)}`
              : undefined
          }
        />
        <KpiCard
          icon={<CalendarRange size={14} />}
          label="This week"
          primary={formatMinutes(info?.secondsThisWeek ?? 0)}
          secondary={`${info?.callsThisWeek ?? 0} ${callsLabel.toLowerCase()}`}
          trend={info?.trendThisWeek ?? 0}
          comparison={
            info?.thisWeekCompare !== undefined && info.thisWeekCompare !== 0
              ? `vs last week ${formatMinutes(info.thisWeekCompare)}`
              : undefined
          }
        />
        <KpiCard
          icon={<CalendarDays size={14} />}
          label="This month"
          primary={formatMinutes(info?.secondsThisMonth ?? 0)}
          secondary={`${info?.callsThisMonth ?? 0} ${callsLabel.toLowerCase()}`}
          trend={info?.trendThisMonth ?? 0}
          comparison={
            info?.thisMonthCompare !== undefined && info.thisMonthCompare !== 0
              ? `vs last month ${formatMinutes(info.thisMonthCompare)}`
              : undefined
          }
        />
      </div>
    </section>
  );
}

function KpiCard({
  icon,
  label,
  primary,
  secondary,
  trend,
  comparison,
}: {
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string;
  trend?: number;
  comparison?: string;
}) {
  const trendIcon =
    trend === undefined || trend === 0 ? (
      <Minus size={12} aria-hidden="true" />
    ) : trend > 0 ? (
      <ArrowUpRight size={12} aria-hidden="true" />
    ) : (
      <ArrowDownRight size={12} aria-hidden="true" />
    );
  const trendColor =
    trend === undefined || trend === 0
      ? "var(--portal-muted)"
      : trend > 0
      ? "#15803d"
      : "#b45309";

  return (
    <article
      style={{
        margin: 0,
        padding: "10px 14px",
        borderRadius: 12,
        background: "transparent",
        border: "1px solid var(--portal-primary-faint)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--portal-muted)",
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 600,
        }}
      >
        <span aria-hidden="true" style={{ display: "inline-flex" }}>{icon}</span>
        <span>{label}</span>
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            color: trendColor,
            fontSize: "0.7rem",
            fontWeight: 700,
          }}
          aria-hidden="true"
        >
          {trendIcon}
        </span>
      </header>
      <div
        style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "var(--portal-primary)",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {primary}
      </div>
      {secondary && (
        <div style={{ color: "var(--portal-muted)", fontSize: "0.75rem", lineHeight: 1.2 }}>
          {secondary}
        </div>
      )}
      {comparison && (
        <div style={{ color: "var(--portal-muted)", fontSize: "0.7rem", lineHeight: 1.2 }}>
          {comparison}
        </div>
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/*                  Per-day inbound minutes (graph)                    */
/* ------------------------------------------------------------------ */

interface MinutesPerDayTileProps {
  /**
   * Live "minutes today" override sourced from the inbound KPI tile
   * (`stats.inbound.secondsToday`). The chart itself is fetched once
   * per page mount and never polls, so this prop is the only way the
   * current-day bar stays current without re-querying the monthly
   * report SP.
   */
  todaySecondsOverride: number | null;
  /** Same idea for the bar's hover tooltip / "calls" totals. */
  todayCallsOverride: number | null;
}

function MinutesPerDayTile({ todaySecondsOverride, todayCallsOverride }: MinutesPerDayTileProps) {
  // Single-shot fetch on mount — the chart deliberately does not
  // auto-refresh while the dashboard is open. The "today" bar is
  // updated locally from the inbound KPI numbers (which the page
  // already polls).
  const [data, setData] = useState<MonthlyTrafficResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    customerDashboardApi
      .monthlyTraffic()
      .then((r) => {
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const days = data?.days ?? [];
  const monthLabel = useMemo(() => {
    if (!data) return "";
    const y = Number(data.year);
    const m = Number(data.month);
    if (!Number.isFinite(y) || y < 1900 || !Number.isFinite(m) || m < 1 || m > 12) return "";
    const d = new Date(y, m - 1, 1);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  }, [data]);

  // Build a fully populated day series so empty days still get a slot
  // on the x-axis. The current day's minutes/calls are overridden
  // from the inbound KPI tile so the chart stays in sync without
  // re-fetching monthly traffic.
  const series = useMemo(() => {
    type Pt = { day: number; minutes: number; calls: number; isToday: boolean };
    if (!data) return [] as Pt[];
    const y = Number(data.year);
    const m = Number(data.month);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return [] as Pt[];

    const lookup = new Map<number, { minutes: number; calls: number }>();
    for (const d of days) {
      const date = new Date(d.date);
      if (!isNaN(date.getTime())) {
        lookup.set(date.getUTCDate(), {
          minutes: Math.round((d.secondsIn || 0) / 60),
          calls: d.callsIn || 0,
        });
      }
    }
    const now = new Date();
    const inThisMonth = now.getFullYear() === y && now.getMonth() + 1 === m;
    const totalDays = new Date(y, m, 0).getDate();
    const out: Pt[] = [];
    for (let i = 1; i <= totalDays; i++) {
      const e = lookup.get(i);
      const isToday = inThisMonth && now.getDate() === i;
      let minutes = e?.minutes ?? 0;
      let calls = e?.calls ?? 0;
      if (isToday) {
        if (todaySecondsOverride != null && todaySecondsOverride >= 0) {
          minutes = Math.round(todaySecondsOverride / 60);
        }
        if (todayCallsOverride != null && todayCallsOverride >= 0) {
          calls = todayCallsOverride;
        }
      }
      out.push({ day: i, minutes, calls, isToday });
    }
    return out;
  }, [data, days, todaySecondsOverride, todayCallsOverride]);

  const maxMinutes = useMemo(
    () => series.reduce((acc, p) => (p.minutes > acc ? p.minutes : acc), 0),
    [series],
  );
  const totalMinutes = useMemo(
    () => series.reduce((acc, p) => acc + p.minutes, 0),
    [series],
  );
  const totalCalls = useMemo(
    () => series.reduce((acc, p) => acc + p.calls, 0),
    [series],
  );

  // Compute the y-axis ticks. The user wants the scale to start at
  // 100 minutes minimum and grow in clean steps as the data does:
  // 100 → 1 000 → 10 000 → +10 000 from there. We then evenly split
  // the chosen ceiling into 4 ticks so the gridlines line up with
  // human-readable numbers (e.g. ceiling 10 000 → 0/2 500/5 000/7 500/10 000).
  const yScale = useMemo(() => makeMinutesScale(maxMinutes), [maxMinutes]);

  return (
    <section className="tile" aria-labelledby="tile-minutes-day">
      <header className="tile-head" style={{ flexWrap: "wrap", gap: 8 }}>
        <h3 id="tile-minutes-day" style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "var(--portal-primary-soft)",
              color: "var(--portal-primary-darker)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <BarChart3 size={18} />
          </span>
          Inbound minutes per day
          <span style={{ color: "var(--portal-muted)", fontWeight: 500, fontSize: "0.8rem" }}>
            {monthLabel}
          </span>
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: "var(--portal-muted)", fontSize: "0.78rem" }}>
            <strong style={{ color: "var(--portal-text)" }}>{totalMinutes.toLocaleString()} min</strong>
            {" · "}
            <strong style={{ color: "var(--portal-text)" }}>{totalCalls.toLocaleString()}</strong> calls
          </span>
          <Link
            to="/reports/inbound"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "var(--portal-primary-darker)",
              textDecoration: "none",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            View more <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      {loading && !data && (
        <div style={{ color: "var(--portal-muted)", padding: "12px 0" }}>Loading minutes…</div>
      )}
      {Boolean(error) && !data && (
        <div style={{ color: "#b91c1c", padding: "12px 0" }}>Could not load monthly inbound minutes.</div>
      )}

      {data && <DayBarChart series={series} yScale={yScale} />}
    </section>
  );
}

interface YAxisScale {
  ceiling: number;
  /** Tick values from 0 → ceiling, low → high. */
  ticks: number[];
}

/**
 * Pick a "nice" y-axis ceiling that satisfies the dashboard's tier
 * rules (≥100, then 1000, then 10000, then +10000 increments) and
 * derive 4 evenly spaced ticks in between. The bottom tick is always
 * 0 so the chart sits flush on the baseline.
 */
function makeMinutesScale(maxValue: number): YAxisScale {
  const v = Math.max(0, maxValue || 0);
  let ceiling: number;
  if (v <= 100) ceiling = 100;
  else if (v <= 1000) ceiling = 1000;
  else if (v <= 10000) ceiling = 10000;
  else ceiling = Math.ceil(v / 10000) * 10000;

  // 5 ticks (incl. 0 and ceiling) keeps the chart legible without
  // crowding the y-axis label column.
  const ticks: number[] = [];
  const steps = 4;
  for (let i = 0; i <= steps; i++) {
    ticks.push(Math.round((ceiling / steps) * i));
  }
  return { ceiling, ticks };
}

function DayBarChart({
  series,
  yScale,
}: {
  series: Array<{ day: number; minutes: number; calls: number; isToday: boolean }>;
  yScale: YAxisScale;
}) {
  const chartHeight = 160;
  const yLabelWidth = 40;
  const minHeightPct = 1.5;

  return (
    <div style={{ display: "flex", gap: 6, paddingTop: 4 }}>
      {/* y-axis labels — anchored to the same chart-area height so
          they sit precisely opposite the gridlines */}
      <div
        aria-hidden="true"
        style={{
          width: yLabelWidth,
          height: chartHeight,
          position: "relative",
          flexShrink: 0,
          color: "var(--portal-muted)",
          fontSize: "0.65rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {yScale.ticks.map((t, i) => {
          // Tick i=0 → bottom, last tick → top. CSS bottom% lines up
          // with the bar tops since both are measured from the
          // baseline.
          const pct = (i / (yScale.ticks.length - 1)) * 100;
          return (
            <div
              key={t}
              style={{
                position: "absolute",
                right: 6,
                bottom: `${pct}%`,
                transform: "translateY(50%)",
                textAlign: "right",
                width: "100%",
              }}
            >
              {t.toLocaleString()}
            </div>
          );
        })}
      </div>

      {/* chart area + x-axis */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          role="img"
          aria-label="Inbound minutes per day"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "flex-end",
            gap: 3,
            height: chartHeight,
          }}
        >
          {/* horizontal gridlines per tick */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          >
            {yScale.ticks.map((t, i) => {
              const pct = (i / (yScale.ticks.length - 1)) * 100;
              return (
                <div
                  key={t}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: `${pct}%`,
                    height: 1,
                    background:
                      i === 0
                        ? "var(--portal-border)"
                        : "var(--portal-primary-faint)",
                  }}
                />
              );
            })}
          </div>

          {series.map((p) => {
            const ratio = yScale.ceiling > 0 ? p.minutes / yScale.ceiling : 0;
            const height = Math.max(
              p.minutes > 0 ? minHeightPct : 0,
              Math.min(100, ratio * 100),
            );
            const fg = p.isToday ? "var(--portal-primary-darker)" : "var(--portal-primary)";
            const bg = p.minutes > 0 ? fg : "var(--portal-primary-faint)";
            return (
              <div
                key={p.day}
                title={`Day ${p.day}: ${p.minutes.toLocaleString()} min · ${p.calls.toLocaleString()} calls`}
                style={{
                  position: "relative",
                  flex: 1,
                  minWidth: 0,
                  height: `${height}%`,
                  background: bg,
                  borderRadius: 4,
                  transition: "background 0.18s ease",
                  outline: p.isToday ? "1px solid var(--portal-primary-darker)" : "none",
                  outlineOffset: 1,
                }}
              />
            );
          })}
        </div>

        {/* x-axis: a label for *every* day (not just every 5th) */}
        <div
          aria-hidden="true"
          style={{
            display: "flex",
            gap: 3,
            color: "var(--portal-muted)",
            fontSize: "0.6rem",
            marginTop: 4,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {series.map((p) => (
            <div
              key={p.day}
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: "center",
                fontWeight: p.isToday ? 700 : 400,
                color: p.isToday ? "var(--portal-primary-darker)" : undefined,
              }}
            >
              {p.day}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                              Helpers                                */
/* ------------------------------------------------------------------ */

/**
 * Duration formatter for the KPI hero numbers — always rendered as
 * `m:ss` (minutes never roll up into hours and the seconds stay as a
 * two-digit suffix). The hero numbers only need to read as a "size"
 * so the legacy `1h 30m` style is intentionally avoided; e.g. 90
 * minutes 5 seconds renders as `90:05`.
 */
function formatMinutes(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm.toLocaleString()}:${ss.toString().padStart(2, "0")}`;
}
